import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Button } from '@material-ui/core';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import LoadingButton from 'components/basic/LoadingButton';
import axiosClient from 'http/axios-client';
import _ from 'lodash';
import { useAlerts } from '../../hooks/alerts-hooks';
import { toISOString } from '../../pages/AppointmentsPage';

const MultipleAppointments = ({ setOpenDialog, openDialog, appointments, refetch, info, close }) => {
  const [ startDate, setStartDate ] = useState(new Date());
  const [ startTime, setStartTime ] = useState(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const [ endDate, setEndDate ] = useState(new Date());
  const [ endTime, setEndTime ] = useState(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const [ selectedDays, setSelectedDays ] = useState([]);
  const [ multipleLoading, setMultipleLoading ] = useState(false);

  const { alertSuccess, alertError } = useAlerts();

  const [ daysOfWeek, setDaysOfWeek ] = useState([
    { name: 'Monday', value: 1, selected: false },
    { name: 'Tuesday', value: 2, selected: false },
    { name: 'Wednesday', value: 3, selected: false },
    { name: 'Thursday', value: 4, selected: false },
    { name: 'Friday', value: 5, selected: false }]);

  const handleDayToggle = (event, newDays) => {
    const updatedDaysOfWeek = daysOfWeek.map((day) => {
      return {
        ...day,
        selected: newDays.includes(day.value),
      };
    });
    const selectedDaysValues = daysOfWeek
      .filter((day) => { return newDays.includes(day.value); })
      .map((day) => { return day.value; });
    setDaysOfWeek(updatedDaysOfWeek);
    setSelectedDays(selectedDaysValues);
  };

  useEffect(() => {
    if ('' !== info) {
      setStartDate(new Date(info.start_date));
      setStartTime(info.start_time);
      setEndDate(new Date(info.end_date));
      setEndTime(info.end_time);
      const days = info.days_of_week.split('-');
      const copy = _.cloneDeep(daysOfWeek);
      days.forEach((dd) => {
        copy.find((x) => { return x.value === Number(dd); }).selected = true;
      });
      setDaysOfWeek(copy);
    }
  }, [ info ]);

  useEffect(() => {
    const updatedSelect = [];
    daysOfWeek.forEach((dd) => {
      if (true === dd.selected) {
        updatedSelect.push(dd.value);
      }
    });
    setSelectedDays(updatedSelect);
  }, daysOfWeek);

  const createMultipleAppointments = async () => {
    if (0 === selectedDays.length) {
      return alertError('Choose days of the week');
    }
    let changes = false;
    setMultipleLoading(true);

    const startDateObj = new Date(startDate);

    const endDateObj = endDate;

    const starting = new Date(startDateObj);

    const ending = new Date(endDateObj);

    startDateObj.setHours(0, 0, 0, 0);

    const appointmentDuration = 15;
    const [ startHour, startMinute ] = startTime.split(':').map(Number);

    const [ endHour, endMinute ] = endTime.split(':').map(Number);
    const multiAppointments = [];

    while (startDateObj <= endDateObj) {
      const currentDayOfWeek = startDateObj.getDay();

      if (selectedDays.includes(currentDayOfWeek)) {
        const startDateTime = new Date(startDateObj);
        startDateTime.setHours(startHour, startMinute, 0, 0);

        const endDateTime = new Date(startDateObj);
        endDateTime.setHours(endHour, endMinute, 0, 0);

        let currentDateTime = new Date(startDateTime);

        while (currentDateTime < endDateTime) {
          const appointment = {
            date_from: toISOString(currentDateTime),
            date_to: toISOString(new Date(currentDateTime.getTime() + appointmentDuration * 60000)),
          };

          if ('' === info) {
            const exists = appointments.some(
              ({ start, end }) => {
                return toISOString(start) === appointment.date_from && toISOString(end) === appointment.date_to;
              },
            );

            if (!exists) {
              multiAppointments.push(appointment);
              changes = true;
            }
          } else {
            multiAppointments.push(appointment);
            changes = true;
          }

          currentDateTime = new Date(currentDateTime.getTime() + appointmentDuration * 60000);
        }
      }

      startDateObj.setDate(startDateObj.getDate() + 1);
    }

    starting.setHours(startHour, startMinute);
    ending.setHours(endHour, endMinute);

    const appointmentsData = { serial_appointment: {
      start_date: toISOString(starting),
      end_date: toISOString(ending),
      start_time: startTime,
      end_time: endTime,
      week_days: selectedDays.join('-'),
    },
    slots: multiAppointments };

    if ('' !== info) {
      return axiosClient.put(`/appointments/series-update/${ info.id }`, appointmentsData)
        .then(() => {
          refetch()
            .then(() => {
              setStartDate(new Date());
              if (changes) {
                alertSuccess('Appointments booked successfuly');
              } else { alertError('Appointments already exist'); }
              setMultipleLoading(false);
              setOpenDialog(false);
              close.close();
            });
        })
        .catch((error) => {
          alertError(error.response.data.message);
        });
    }
    return axiosClient.post('/appointments/recurrent-slots', appointmentsData)
      .then(() => {
        refetch()
          .then(() => {
            setStartDate(new Date());
            if (changes) {
              alertSuccess('Appointments booked successfuly');
            } else { alertError('Appointments already exist'); }
            setMultipleLoading(false);
            setOpenDialog(false);
          });
      })
      .catch((error) => {
        console.log(error);
        alertError(error.response.data.message);
      });
  };

  return (
    <Dialog
      onClose={ () => { setOpenDialog(false); } }
      open={ openDialog }
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>Add multiple appointments</DialogTitle>
      <DialogContent>

        <LocalizationProvider dateAdapter={ AdapterDateFns }>
          <Grid container spacing={ 2 }>
            <Grid item xs={ 6 }>
              <DatePicker
                renderInput={ (inputProps) => {
                  return (<TextField { ...inputProps } variant='outlined' />);
                } }
                slotProps={ { textField: { variant: 'outlined' } } }
                inputVariant='outlined'
                autoOk
                fullWidth
                value={ startDate }
                onChange={ setStartDate }
                label='Start'
              />
            </Grid>

            <Grid item xs={ 6 }>
              <DatePicker
                renderInput={ (inputProps) => {
                  return (<TextField { ...inputProps } variant='outlined' />);
                } }
                slotProps={ { textField: { variant: 'outlined' } } }
                autoOk
                inputVariant='outlined'
                fullWidth
                value={ endDate }
                onChange={ setEndDate }
                label='End'
              />
            </Grid>

            <Grid item xs={ 12 } style={ { display: 'flex', justifyContent: 'center' } }>
              <ToggleButtonGroup
                value={ selectedDays }
                onChange={ handleDayToggle }
                aria-label='Days of Week'
              >
                {daysOfWeek.map((day) => {
                  return (
                    <ToggleButton
                      key={ day.value }
                      value={ day.value }
                      selected={ day.selected }

                    >
                      {day.name}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={ 6 } style={ { display: 'flex', justifyContent: 'flex-end' } }>
              <TextField
                type='time'
                variant='outlined'
                value={ startTime }
                onChange={ (event) => { return setStartTime(event.target.value); } }
              />
            </Grid>

            <Grid item xs={ 6 } style={ { display: 'flex', justifyContent: 'flex-start' } }>
              <TextField
                type='time'
                variant='outlined'
                value={ endTime }
                onChange={ (event) => { return setEndTime(event.target.value); } }
              />
            </Grid>

          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={ () => { setOpenDialog(false); } }>Cancel</Button>
        <LoadingButton
          loading={ multipleLoading }
          label='Add'
          onClick={ createMultipleAppointments }

        />

      </DialogActions>
    </Dialog>
  );
};

export default MultipleAppointments;
